import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Box, Button, Chip, CircularProgress, Collapse, Dialog, IconButton,
  LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import {
  AutoAwesome, CheckCircle, Close, DeleteOutline, ErrorOutline, ExpandLess, ExpandMore,
  FileDownload, HelpOutline, Refresh, Send, WarningAmber,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { calMarkdownComponents } from './CalMarkdown';
import { useCalChat } from '../contexts/CalChatContext';
import { useCalWebSocket } from '../contexts/CalWebSocketContext';
import { CalWebSocketMessage } from '../services/calWebSocketService';
import { getCalAvatar } from '../config/calAvatars';
import {
  ChecklistItem, ChecklistNarrative, ChecklistStatus, MBAuthError,
  MonthEndChecklist as Checklist, fetchChecklistNarrative, fetchMonthEndChecklist,
} from '../utils/massBalanceStore';
import { exportChecklistToExcel } from '../utils/checklistExport';

/**
 * Month End Checklist — the eleven checks that close a month, with a line to Cal beside them.
 *
 * These eleven questions were being typed at Cal one at a time every month end. They are computed
 * server-side now (get_month_end_checklist), because a checklist is a CONTROL: it has to return the
 * same answer for the same data every month, and it has to be quick enough to just open. Cal is kept
 * for the part he is actually better at — talking through what a finding means.
 *
 * Three columns, not stacked: a compact status rail you can take in at a glance, the detail, and the
 * chat. The first build stacked the chat under the checklist and both were half-visible, which is
 * the opposite of useful for a screen whose whole job is "what do I still have to deal with".
 *
 * The chat is SCOPED TO THIS PANEL and does not show the global Cal thread. Two parts to that:
 *
 *   1. Its own message list, so an unrelated meter-health answer never appears next to a density
 *      finding (which is exactly what the shared list did).
 *   2. Its own Bedrock session (`<unified>-month-end`), so Cal's memory here is the close and
 *      nothing else, while follow-ups within the panel still chain normally.
 *
 * Handler ownership: ModernCalChat's socket handler already returns early unless activeView is
 * 'floating'. Claiming activeView 'checklist' while this panel is open therefore stands it down and
 * lets this panel own the replies, with no change to ModernCalChat and no risk of a reply being
 * added twice. The previous view is restored on close.
 */

const STATUS: Record<ChecklistStatus, { label: string; short: string; color: 'error' | 'warning' | 'success' | 'default'; icon: React.ReactElement }> = {
  fail:    { label: 'Needs fixing', short: 'Fix',     color: 'error',   icon: <ErrorOutline fontSize="small" /> },
  warn:    { label: 'Confirm',      short: 'Confirm', color: 'warning', icon: <WarningAmber fontSize="small" /> },
  ok:      { label: 'Passed',       short: 'Passed',  color: 'success', icon: <CheckCircle fontSize="small" /> },
  no_data: { label: 'No data',      short: 'No data', color: 'default', icon: <HelpOutline fontSize="small" /> },
};

const tone = (theme: any, c: 'error' | 'warning' | 'success' | 'default') =>
  c === 'default' ? theme.palette.text.disabled : theme.palette[c].main;

// Finding rows differ per check (density pairs, inventory breaks, trend series), so they render
// from whatever keys a row actually has rather than a fixed column set per check.
const prettyKey = (k: string) =>
  k.replace(/_/g, ' ')
   .replace(/\bpct\b/g, '%')
   // "rho" used to render as the Greek letter, which the following capitalisation then turned into
   // a capital Rho -- visually identical to a Latin "P", so the column just read "Ρ".
   .replace(/\brho\b/g, 'density')
   .replace(/^./, (c) => c.toUpperCase());

const fmtVal = (v: any): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') {
    if (Number.isInteger(v) && Math.abs(v) >= 1000) return v.toLocaleString();
    return Math.abs(v) < 1 && v !== 0 ? String(Number(v.toFixed(4))) : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
};

const FindingsTable: React.FC<{ item: ChecklistItem }> = ({ item }) => {
  const theme = useTheme();
  const cols = useMemo(() => {
    const seen: string[] = [];
    item.findings.forEach((r) => Object.keys(r).forEach((k) => { if (!seen.includes(k)) seen.push(k); }));
    return seen;
  }, [item.findings]);
  if (!item.findings.length) return null;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { fontSize: '0.74rem', py: 0.4, px: 0.8, whiteSpace: 'nowrap' } }}>
        <TableHead>
          <TableRow>
            {cols.map((c) => (
              <TableCell key={c} sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {prettyKey(c)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {item.findings.map((r, i) => (
            <TableRow key={i}>{cols.map((c) => <TableCell key={c}>{fmtVal(r[c])}</TableCell>)}</TableRow>
          ))}
        </TableBody>
      </Table>
      {item.finding_count > item.findings.length && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.8 }}>
          Showing {item.findings.length} of {item.finding_count}. Ask Cal for the full list.
        </Typography>
      )}
    </Box>
  );
};

interface ChatMsg { id: string; text: string; sender: 'user' | 'cal'; }


interface Props {
  open: boolean;
  onClose: () => void;
  dataset: string;
  period: string;
  /** 'refinery' (Mass Balance tab) or 'oas' (Diagnostic tab). */
  utility: string;
}

const MonthEndChecklist: React.FC<Props> = ({ open, onClose, dataset, period, utility }) => {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const { getIdTokenClaims } = useAuth0();
  const { sessionId, viewContext, activeView, setActiveView } = useCalChat();
  const { sendMessage: sendWs, isConnected, addMessageHandler } = useCalWebSocket();

  const [data, setData] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [streaming, setStreaming] = useState('');
  const [thinking, setThinking] = useState(false);
  // Bumped by "clear", which rotates the Bedrock session id below.
  const [chatEpoch, setChatEpoch] = useState(0);
  // Cal's write-up of the close: one overview + one short summary per check, from a single direct
  // model call. Pre-loaded, so there is nothing to click. Cached per dataset+period for the session
  // so reopening the panel does not pay for it again.
  const [narrative, setNarrative] = useState<ChecklistNarrative | null>(null);
  const [narrLoading, setNarrLoading] = useState(false);
  const [narrError, setNarrError] = useState<string | null>(null);
  const narrCache = useRef<Record<string, ChecklistNarrative>>({});

  const detailRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const chatEnd = useRef<HTMLDivElement>(null);
  const streamRef = useRef('');

  const activeRef = useRef(activeView);
  activeRef.current = activeView;

  // This panel's own Cal conversation, separate from the floating chat's. Clearing ROTATES this id
  // rather than only emptying the list: Bedrock keys agent history on the session, so reusing the id
  // would leave Cal still remembering everything the user just cleared, and he would answer the next
  // question by referring back to it.
  const chatSession = `${sessionId}-month-end${chatEpoch ? `-${chatEpoch}` : ''}`;

  const clearChat = useCallback(() => {
    setMsgs([]); setStreaming(''); streamRef.current = ''; setThinking(false);
    setChatEpoch((e) => e + 1);
  }, []);

  // Claim the socket while open (see the note at the top), and hand it back on close.
  useEffect(() => {
    if (!open) return;
    const prior = activeRef.current;
    setActiveView('checklist');
    return () => setActiveView(prior === 'checklist' ? 'none' : prior);
  }, [open, setActiveView]);

  // Both Mass Balance tabs mount this component, so the handler is registered ONLY while a panel is
  // actually open -- otherwise the hidden tab's copy would quietly collect every reply too, and show
  // a stale conversation the next time it was opened.
  useEffect(() => {
    if (!open) return;
    const onMsg = (d: CalWebSocketMessage) => {
      if (activeRef.current !== 'checklist') return;      // the floating chat owns it, not us
      switch (d.type) {
        case 'status':
          setThinking(true); break;
        case 'chunk':
          streamRef.current += d.content || '';
          setStreaming(streamRef.current); break;
        case 'complete': {
          const txt = d.message || (d as any).response || streamRef.current;
          setMsgs((p) => [...p, { id: `c-${Date.now()}`, sender: 'cal', text: txt || 'I ran into an issue answering that.' }]);
          streamRef.current = ''; setStreaming(''); setThinking(false); break;
        }
        case 'error':
          setMsgs((p) => [...p, { id: `e-${Date.now()}`, sender: 'cal',
            text: d.message || (d as any).error || 'Something went wrong. Try again.' }]);
          streamRef.current = ''; setStreaming(''); setThinking(false); break;
      }
    };
    return addMessageHandler(onMsg);
  }, [addMessageHandler, open]);

  const load = useCallback(async () => {
    if (!dataset || !period) return;
    setLoading(true); setError(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const res = await fetchMonthEndChecklist(dataset, period, utility, token);
      setData(res);
      setExpanded(Object.fromEntries(res.items.map((i) => [i.id, i.status === 'fail' || i.status === 'warn'])));
    } catch (e: any) {
      setError(e instanceof MBAuthError ? 'Your session expired — reload the page and sign in again.'
                                        : (e?.message || 'Could not run the checklist.'));
    } finally { setLoading(false); }
  }, [dataset, period, utility, getIdTokenClaims]);

  // Cal's write-up, fetched once the checklist is on screen. Deliberately decoupled: the checklist
  // is the control and must render instantly: this is commentary on top of it, so a failure here
  // costs prose and nothing else.
  useEffect(() => {
    if (!open || !data) return;
    const key = `${data.dataset}|${data.period}|${data.utility}`;
    const hit = narrCache.current[key];
    if (hit) { setNarrative(hit); setNarrError(null); return; }
    let cancelled = false;
    setNarrLoading(true); setNarrError(null); setNarrative(null);
    (async () => {
      try {
        const token = (await getIdTokenClaims())?.__raw;
        const n = await fetchChecklistNarrative(data.dataset, data.period, data.utility, data, token);
        if (cancelled) return;
        narrCache.current[key] = n;
        setNarrative(n);
      } catch (e: any) {
        if (!cancelled) setNarrError(e?.message || 'Cal could not summarise this close.');
      } finally {
        if (!cancelled) setNarrLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, data, getIdTokenClaims]);

  useEffect(() => { if (open) void load(); }, [open, load]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, streaming]);

  const ask = useCallback((text: string) => {
    if (!text.trim()) return;
    setMsgs((p) => [...p, { id: `u-${Date.now()}`, sender: 'user', text }]);
    setThinking(true);
    sendWs(text, { sessionId: chatSession, activeView: 'checklist', ...viewContext }, chatSession);
    setInput('');
  }, [sendWs, chatSession, viewContext]);

  // Hand Cal the finding itself, not just the question, so he does not re-derive it and word it
  // differently from what is on screen.
  const discuss = (item: ChecklistItem) => ask(
    `Month End Checklist — ${item.group} / ${item.label} came back "${STATUS[item.status].label}": `
    + `${item.headline} ${item.follow_up}`,
  );

  const jumpTo = (id: string) => {
    setExpanded((p) => ({ ...p, [id]: true }));
    requestAnimationFrame(() => itemRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.groups.map((g) => ({ group: g, items: data.items.filter((i) => i.group === g) }))
      .filter((g) => g.items.length);
  }, [data]);

  const attention = (data?.summary.fail || 0) + (data?.summary.warn || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
      PaperProps={{ sx: { width: '96vw', height: '94vh', maxWidth: 1680, display: 'flex', flexDirection: 'column' } }}>

      {/* ---- header ---- */}
      <Box sx={{ px: 2, py: 1.25, bgcolor: theme.palette.primary.main, color: '#fff', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Month End Checklist</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {dataset}{period ? ` · ${period}` : ''}{data?.prior_period ? ` · compared with ${data.prior_period}` : ''}
            </Typography>
          </Box>
          {data && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {(['fail', 'warn', 'ok', 'no_data'] as ChecklistStatus[])
                .filter((k) => (data.summary[k] || 0) > 0)
                .map((k) => (
                  <Chip key={k} size="small" label={`${data.summary[k]} ${STATUS[k].label.toLowerCase()}`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, height: 22 }} />
                ))}
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          {data && (
            <Button size="small" variant="outlined" startIcon={<FileDownload fontSize="small" />}
              onClick={() => exportChecklistToExcel(data, narrative)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', textTransform: 'none', whiteSpace: 'nowrap',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              Export Checklist to Excel
            </Button>
          )}
          <Tooltip title="Re-run the checklist">
            <span><IconButton size="small" onClick={() => void load()} disabled={loading} sx={{ color: '#fff' }}>
              <Refresh fontSize="small" /></IconButton></span>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><Close fontSize="small" /></IconButton>
        </Stack>
      </Box>
      {loading && <LinearProgress sx={{ flexShrink: 0 }} />}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: wide ? 'row' : 'column', minHeight: 0 }}>

        {/* ---- 1. compact status rail: the whole close at a glance ---- */}
        {wide && data && (
          <Box sx={{ width: 268, flexShrink: 0, overflowY: 'auto', borderRight: `1px solid ${theme.palette.divider}`, py: 1 }}>
            {grouped.map(({ group, items }) => (
              <Box key={group} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ px: 1.5, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
                  {group.toUpperCase()}
                </Typography>
                {items.map((i) => {
                  const st = STATUS[i.status];
                  return (
                    <Stack key={i.id} direction="row" alignItems="center" spacing={1}
                      onClick={() => jumpTo(i.id)}
                      sx={{
                        px: 1.5, py: 0.6, cursor: 'pointer', borderLeft: `3px solid ${tone(theme, st.color)}`,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                      }}>
                      <Box sx={{ color: tone(theme, st.color), display: 'flex' }}>{st.icon}</Box>
                      <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.25,
                        fontWeight: i.status === 'fail' || i.status === 'warn' ? 600 : 400 }}>
                        {i.label}
                      </Typography>
                      {i.finding_count > 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.66rem' }}>
                          {i.finding_count}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}

        {/* ---- 2. detail ---- */}
        <Box ref={detailRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, minHeight: 0 }}>
          {error && <Alert severity="error" sx={{ mb: 1 }} action={<Button size="small" onClick={() => void load()}>Retry</Button>}>{error}</Alert>}
          {!error && !data && loading && (
            <Stack alignItems="center" sx={{ py: 5 }} spacing={1}>
              <CircularProgress size={26} />
              <Typography variant="caption" color="text.secondary">Running eleven checks…</Typography>
            </Stack>
          )}
          {data && (
            <>
              <Alert severity={attention ? (data.summary.fail ? 'error' : 'warning') : 'success'} sx={{ mb: 1.5, py: 0.5 }}>
                {data.verdict}
              </Alert>
              {grouped.map(({ group, items }) => (
                <Box key={group} sx={{ mb: 1.75 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6 }}>
                    {group}
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    {items.map((item) => {
                      const st = STATUS[item.status];
                      const isOpen = !!expanded[item.id];
                      return (
                        <Box key={item.id} ref={(el: HTMLDivElement | null) => { itemRefs.current[item.id] = el; }}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`, borderRadius: 1, scrollMarginTop: 8,
                            borderLeft: `3px solid ${tone(theme, st.color)}`,
                            bgcolor: item.status === 'ok' ? 'transparent' : alpha(tone(theme, st.color), 0.04),
                          }}>
                          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ p: 1, cursor: 'pointer' }}
                            onClick={() => setExpanded((p) => ({ ...p, [item.id]: !p[item.id] }))}>
                            <Box sx={{ color: tone(theme, st.color), mt: '1px' }}>{st.icon}</Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{item.label}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                                {item.headline}
                              </Typography>
                            </Box>
                            {item.finding_count > 0 && (
                              <Badge badgeContent={item.finding_count} color={st.color === 'default' ? 'primary' : st.color}
                                sx={{ mr: 0.5, '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }} />
                            )}
                            {isOpen ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
                          </Stack>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ px: 1, pb: 1 }}>
                              {(() => {
                                const cal = narrative?.items?.[item.id];
                                if (cal) {
                                  return (
                                    <Box sx={{
                                      mb: 1.25, p: 1.25, borderRadius: 1,
                                      bgcolor: alpha(theme.palette.primary.main, 0.045),
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                                    }}>
                                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                                        <AutoAwesome sx={{ fontSize: 14, color: 'primary.main' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>What this is showing</Typography>
                                      </Stack>
                                      <Box sx={{
                                        fontSize: '0.84rem',
                                        '& p': { mt: 0, mb: 0.5 }, '& ul, & ol': { mt: 0, mb: 0, pl: 2.2 },
                                        '& li': { mb: 0.35 },
                                      }}>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={calMarkdownComponents}>{cal}</ReactMarkdown>
                                      </Box>
                                    </Box>
                                  );
                                }
                                if (narrLoading) {
                                  return (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                                      <CircularProgress size={12} />
                                      <Typography variant="caption" color="text.secondary">Cal is reading this check&hellip;</Typography>
                                    </Stack>
                                  );
                                }
                                return null;
                              })()}
                              <FindingsTable item={item} />
                              <Button size="small" onClick={() => discuss(item)} sx={{ mt: 1, textTransform: 'none' }}
                                startIcon={<Box component="img" src={getCalAvatar().path} alt="" sx={{ width: 16, height: 16, borderRadius: '50%' }} />}>
                                Ask in chat
                              </Button>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </Box>

        {/* ---- 3. Cal, scoped to this checklist ---- */}
        <Box sx={{
          width: wide ? '32%' : '100%', minWidth: wide ? 340 : 0, maxWidth: wide ? 520 : 'none',
          flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: wide ? 0 : 240,
          borderLeft: wide ? `1px solid ${theme.palette.divider}` : 'none',
          borderTop: wide ? 'none' : `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.9, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box component="img" src={getCalAvatar().path} alt="" sx={{ width: 22, height: 22, borderRadius: '50%' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>Ask Cal about this close</Typography>
            <Tooltip title={msgs.length ? 'Clear this conversation' : 'Nothing to clear'}>
              <span>
                <IconButton size="small" onClick={clearChat} disabled={!msgs.length && !streaming}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1, minHeight: 0 }}>
            {/* Cal's opening word on the close. Rendered ahead of the thread rather than inside it,
                so it survives "clear" — it is context for the close, not part of the conversation. */}
            {narrLoading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Cal is reviewing the close&hellip;</Typography>
              </Stack>
            )}
            {narrError && !narrLoading && (
              <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
                <Typography variant="caption">
                  Cal could not write the overview this time — the checks above are unaffected.
                </Typography>
              </Alert>
            )}
            {narrative?.overview && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <Box sx={{
                  maxWidth: '96%', px: 1.25, py: 1, borderRadius: 1.5,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                    <Box component="img" src={getCalAvatar().path} alt="" sx={{ width: 18, height: 18, borderRadius: '50%' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Where this close stands</Typography>
                  </Stack>
                  <Box sx={{
                    fontSize: '0.82rem',
                    '& p': { mt: 0, mb: 0.5 }, '& ul, & ol': { mt: 0, mb: 0.5, pl: 2.2 }, '& li': { mb: 0.35 },
                    '& strong': { fontWeight: 700 },
                  }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={calMarkdownComponents}>{narrative.overview}</ReactMarkdown>
                  </Box>
                </Box>
              </Box>
            )}
            {!msgs.length && !streaming && !narrLoading && (
              <Typography variant="caption" color="text.secondary">
                Open any check and choose <strong>Ask in chat</strong>, or ask your own question — about a
                problem or a pass. This conversation covers this month-end close only.
              </Typography>
            )}
            {msgs.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start', mb: 0.75 }}>
                <Box sx={{
                  maxWidth: '92%', px: 1.25, py: 0.75, borderRadius: 1.5,
                  bgcolor: m.sender === 'user' ? theme.palette.primary.main : theme.palette.background.paper,
                  color: m.sender === 'user' ? '#fff' : 'text.primary',
                  border: m.sender === 'user' ? 'none' : `1px solid ${theme.palette.divider}`,
                }}>
                  {m.sender === 'cal' ? (
                    <Box sx={{
                      fontSize: '0.82rem',
                      '& p': { m: 0, mb: 0.5 }, '& ul, & ol': { m: 0, pl: 2 },
                      '& table': { borderCollapse: 'collapse', width: '100%', mt: 1, mb: 1 },
                      '& th, & td': { border: `1px solid ${theme.palette.divider}`, p: 0.4, fontSize: '0.74rem' },
                      '& th': { backgroundColor: alpha(theme.palette.primary.main, 0.05), fontWeight: 600 },
                      '& h1, & h2, & h3': { fontSize: '0.9rem', mt: 1, mb: 0.5 },
                    }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={calMarkdownComponents}>{m.text}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{m.text}</Typography>
                  )}
                </Box>
              </Box>
            ))}
            {streaming && (
              <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper, fontSize: '0.82rem', mb: 0.75 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={calMarkdownComponents}>{streaming}</ReactMarkdown>
              </Box>
            )}
            {thinking && !streaming && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <CircularProgress size={13} />
                <Typography variant="caption" color="text.secondary">Cal is working on it…</Typography>
              </Stack>
            )}
            <div ref={chatEnd} />
          </Box>

          <Box sx={{ p: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            {!isConnected && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5 }}>Reconnecting to Cal…</Typography>
            )}
            <Stack direction="row" spacing={0.75}>
              <TextField fullWidth size="small" multiline maxRows={4} value={input}
                placeholder="Ask Cal about the checklist…"
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input); } }} />
              <IconButton color="primary" disabled={!input.trim() || !isConnected} onClick={() => ask(input)}>
                <Send fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MonthEndChecklist;
